// Company store editor — the store's Orders (UX review, CEO 2026-10-12;
// spec docs/company-stores-mvp.md "Surfaces": a store's orders tab).
//
//   GET  /company-stores/:id/orders                 every order in the store
//   POST /company-stores/:id/orders/:orderId/place  "Place again" (idempotent —
//        never a second sales order). An order whose payment didn't match
//        (paymentProblem) is placed only with { confirmPayment: true }, after
//        the rep ticks "I've checked this payment in Stripe".
//
// A placed order links to its sales order (/orders/:id redirects into the
// project). Reads: any staff. Place again: the store editor roles.
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { StatusPill } from "@/components/ui/StatusPill";
import { api, apiErrorMessage } from "@/lib/api";
import {
  NOT_SET_UP_COPY,
  ORDER_PAID_BY,
  isNotSetUp,
  money,
  shortDate,
  storeOrderStatus,
  type StaffStoreOrder,
} from "@/lib/companyStores";

/** A server sentence without its full stop, to run into ours. */
const clause = (t: string) => t.trim().replace(/[.\s]+$/, "");
const errText = (e: unknown, fallback: string) => (isNotSetUp(e) ? NOT_SET_UP_COPY : apiErrorMessage(e, fallback));

export function StoreOrdersSection({ storeId, canEdit }: { storeId: string; canEdit: boolean }) {
  const q = useQuery({
    queryKey: ["company-stores", "orders", storeId],
    queryFn: () => api<{ orders: StaffStoreOrder[] }>(`/company-stores/${storeId}/orders`),
    retry: (n, e) => !isNotSetUp(e) && n < 2,
  });

  if (q.isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading orders…
      </p>
    );
  }
  if (q.isError) {
    return isNotSetUp(q.error) ? (
      <p role="status" className="text-sm text-muted">{NOT_SET_UP_COPY}</p>
    ) : (
      <p role="alert" className="text-sm text-risk">{errText(q.error, "We couldn't load this store's orders.")}</p>
    );
  }
  const orders = q.data?.orders ?? [];
  if (!orders.length) return <p className="text-sm text-muted">No orders yet.</p>;

  return (
    <div className={TABLE_WRAP}>
      <table className="w-full text-left">
        <thead className={TABLE_HEAD}>
          <tr>
            <th scope="col" className={TH}>Order</th>
            <th scope="col" className={TH}>Who</th>
            <th scope="col" className={`${TH} hidden sm:table-cell`}>Date</th>
            <th scope="col" className={`${TH} text-right`}>Total</th>
            <th scope="col" className={TH}>Status</th>
            <th scope="col" className={`${TH} hidden md:table-cell`}>How paid</th>
          </tr>
        </thead>
        <tbody className="text-[13px]">
          {orders.map((o) => (
            <OrderRows key={o.id} storeId={storeId} order={o} canEdit={canEdit} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrderRows({ storeId, order: o, canEdit }: { storeId: string; order: StaffStoreOrder; canEdit: boolean }) {
  const qc = useQueryClient();
  const [checked, setChecked] = useState(false);
  const [result, setResult] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const status = storeOrderStatus(o);
  const who = o.member.name || o.member.email || "Someone";
  const stuck = !o.salesOrderId && (!!o.placementError || !!o.paymentProblem);

  const place = useMutation({
    mutationFn: () =>
      api<{ status: "placed"; salesOrderId: string; created: boolean }>(`/company-stores/${storeId}/orders/${o.id}/place`, {
        method: "POST",
        body: JSON.stringify(o.paymentProblem ? { confirmPayment: true } : {}),
      }),
    onSuccess: () => {
      setResult({ kind: "ok", text: "Placed. The sales order is in Flow and on the Production Report." });
      void qc.invalidateQueries({ queryKey: ["company-stores", "orders", storeId] });
      void qc.invalidateQueries({ queryKey: ["company-stores", "list"] });
    },
    onError: (e) => {
      setResult({ kind: "err", text: errText(e, "We couldn't place it. Please try again.") });
      void qc.invalidateQueries({ queryKey: ["company-stores", "orders", storeId] });
    },
  });
  const blockedByCheck = !!o.paymentProblem && !checked;

  return (
    <Fragment>
      <tr className={stuck || o.rejectedReason ? "border-b-0" : TROW}>
        <td className={`${TD} font-medium text-ink`}>
          {o.salesOrderId ? (
            <Link to={`/orders/${o.salesOrderId}`} className="hover:underline">
              {o.reference}
              {o.orderNumber ? <span className="block text-[12px] font-normal text-muted">Sales order #{o.orderNumber}</span> : null}
            </Link>
          ) : (
            o.reference
          )}
        </td>
        <td className={TD}>
          <span className="text-ink">{who}</span>
          {o.member.name && o.member.email ? <span className="block text-[12px] text-muted">{o.member.email}</span> : null}
        </td>
        <td className={`${TD} hidden text-muted sm:table-cell`}>{shortDate(o.createdAt)}</td>
        <td className={`${TD} text-right tabular-nums text-ink`}>{money(o.total)}</td>
        <td className={TD}>
          <StatusPill tone={status.tone} className="normal-case">{status.text}</StatusPill>
        </td>
        <td className={`${TD} hidden text-muted md:table-cell`}>{o.paymentMethod ? ORDER_PAID_BY[o.paymentMethod] : "—"}</td>
      </tr>
      {o.status === "rejected" && o.rejectedReason ? (
        <tr className={TROW}>
          <td colSpan={6} className="px-[18px] pb-[15px] pt-0 text-[13px] text-muted">Reason given: “{o.rejectedReason}”</td>
        </tr>
      ) : null}
      {stuck ? (
        <tr className={TROW}>
          <td colSpan={6} className="px-[18px] pb-[15px] pt-0">
            <div className="space-y-2 rounded-md border border-warn/40 bg-warn-bg px-4 py-3 text-[13px] text-ink">
              {o.paymentProblem ? (
                <p className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" aria-hidden />
                  <span>
                    <span className="font-semibold text-warn">The payment doesn't match this order, so it wasn't placed.</span>{" "}
                    {clause(o.paymentProblem)}. Check it in Stripe before placing (or refund it there).
                  </span>
                </p>
              ) : null}
              {o.placementError ? (
                <p className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" aria-hidden />
                  <span>
                    <span className="font-semibold text-warn">It couldn't become a sales order:</span> {clause(o.placementError)}. Nothing was lost. Fix the problem, then place it again.
                  </span>
                </p>
              ) : null}
              {canEdit ? (
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  {o.paymentProblem ? (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-ink"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                        disabled={place.isPending}
                      />
                      <span>I've checked this payment in Stripe</span>
                    </label>
                  ) : null}
                  <Button size="sm" onClick={() => { setResult(null); place.mutate(); }} disabled={place.isPending || blockedByCheck}>
                    {place.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                    {place.isPending ? "Placing…" : "Place again"}
                  </Button>
                </div>
              ) : (
                <p className="text-muted">A rep or admin can place it again.</p>
              )}
              {result ? (
                <p role={result.kind === "err" ? "alert" : "status"} className={result.kind === "err" ? "text-risk" : "text-ink"}>{result.text}</p>
              ) : null}
            </div>
          </td>
        </tr>
      ) : result?.kind === "ok" ? (
        <tr className={TROW}>
          <td colSpan={6} role="status" className="px-[18px] pb-[15px] pt-0 text-[13px] text-ink">{result.text}</td>
        </tr>
      ) : null}
    </Fragment>
  );
}
